import { ActionIcon, Group, Switch, Tooltip } from "@mantine/core";
// Live's Ad copies icon is lucide's text-select, now named SquareDashedText.
import { Palette, SquareDashedText, X } from "lucide-react";

import { Pill } from "../../../ui/Pill";
import { AD_COPIES_TIP } from "./advanced";
import classes from "./image.module.css";

export interface AdvancedSwitchProps {
  on: boolean;
  onChange: (on: boolean) => void;
  /** No model makes ad creatives: the switch stays off. */
  disabled?: boolean;
}

/** Live's "Advanced" switch at the image composer's top left (capture 24). */
export function AdvancedSwitch({ on, onChange, disabled = false }: AdvancedSwitchProps) {
  return (
    <Switch
      label="Advanced"
      size="sm"
      checked={on}
      disabled={disabled}
      onChange={(event) => onChange(event.currentTarget.checked)}
    />
  );
}

export interface AdvancedChipsProps {
  /** Live's `isCanSetupTextCopies`: a product, and a reference or a Style. */
  canSetup: boolean;
  /** A reference is analysed: Ad copies waits, greyed with no tooltip, as live's. */
  analysing: boolean;
  /** The combinations ticked: Ad copies is filled with its x while there are some. */
  total: number;
  onOpenCopies: () => void;
  onClearCopies: () => void;
  /** The Style saved so far, and whether its editor is open. */
  style: string;
  styleOpen: boolean;
  onToggleStyle: () => void;
  onResetStyle: () => void;
}

/**
 * Live's two chips beside the switch (`advanced-configuration`, captures 24, 25 and
 * advanced-234/24): "Ad copies", grey with live's tooltip until the setup allows it, then the
 * modal's door, filled with an x once copies are ticked; and "Style", which opens its editor,
 * filled while it is open or saved, with an x that clears a saved Style.
 */
export function AdvancedChips(props: AdvancedChipsProps) {
  const { canSetup, analysing, total, onOpenCopies, onClearCopies } = props;
  const { style, styleOpen, onToggleStyle, onResetStyle } = props;
  const adCopies = { label: "Ad copies", icon: <SquareDashedText size={20} strokeWidth={1.5} /> };
  const copiesFilled = total > 0;
  const styleFilled = style !== "" && !styleOpen;
  return (
    <Group gap={8} wrap="nowrap">
      {canSetup ? (
        <span className={classes.chipWrap} data-filled={copiesFilled || undefined}>
          <Pill {...adCopies} active={copiesFilled} disabled={analysing} onClick={onOpenCopies} />
          {copiesFilled ? (
            <ActionIcon
              variant="transparent"
              size="sm"
              className={classes.chipReset}
              aria-label="Clear the Ad copies"
              onClick={onClearCopies}
            >
              <X size={16} aria-hidden />
            </ActionIcon>
          ) : null}
        </span>
      ) : (
        <Tooltip label={AD_COPIES_TIP} withArrow multiline maw={320}>
          {/* A disabled button takes no hover, so the tooltip hangs from its wrapper. */}
          <span className={classes.chipWrap}>
            <Pill {...adCopies} disabled />
          </span>
        </Tooltip>
      )}
      <span className={classes.chipWrap} data-filled={styleFilled || undefined}>
        <Pill
          label="Style"
          icon={<Palette size={20} strokeWidth={1.5} />}
          active={styleOpen || style !== ""}
          aria-pressed={styleOpen}
          onClick={onToggleStyle}
        />
        {styleFilled ? (
          <ActionIcon
            variant="transparent"
            size="sm"
            className={classes.chipReset}
            aria-label="Clear the Style"
            onClick={onResetStyle}
          >
            <X size={16} aria-hidden />
          </ActionIcon>
        ) : null}
      </span>
    </Group>
  );
}
